// OfflineBanner — DropPin v0.118.0, v0.171.0
// ---------------------------------------------------------------------
// Thin strip below the header that reports on the offline outbox, plus
// an expandable panel that says WHAT is in it.
//
// v0.17.3 shipped the strip as a bare counter: "3 changes waiting to
// sync". That is the least useful true statement the app could make —
// it tells you something is pending without telling you what, when it
// was made, or what happened to it afterwards. Pressing Sync then
// emptied the queue with no record at all, and a change the server
// rejected was dropped with a console.warn no user will ever read.
//
// v0.118.0:
//   • The strip expands into a list of the queued changes in plain
//     English, each with when it was queued.
//   • Every replay is recorded — applied AND rejected — and the result
//     survives a reload, so "what did that sync just do?" is answerable
//     after the fact.
//   • Changes queued under a DIFFERENT sign-in are shown separately and
//     are never sent. See outbox-identity.js for why.
//
// v0.171.0 — SITE MODE (Brett: "make it offline then push all at once when
// I'm connected to proper wifi"):
//   • The strip is where site mode lives: a per-device switch between
//     "send as I go" and "hold until I tap Sync" (see syncMode.js), and the
//     Sync now button that releases the queue.
//   • It counts what it holds in the product's own nouns — "3 pins, 12
//     photos (18 MB)" — not "15 changes".
//   • While a sync runs it shows progress (n of total, what is going now)
//     with a bar; when a sync stops short it says so and offers Retry
//     instead of quietly waiting for the next 'online' event.
//   • With auto-sync-on-Wi-Fi on, a phone that positively reports Wi-Fi
//     (Android) releases the queue itself; iOS cannot say, so there the tap
//     is the release — and the panel says exactly that.
//
// v0.256.0 — a sync the iPad's sleep interrupted carries on by itself: on
// waking, on 'online', on opening the app again, and on a back-off while it
// waits (resumeIfWanted below; the note it reads is outbox.js getSyncResume).
//
// Calm by default: with an empty queue, no blocked changes and nothing
// new to report, this component renders nothing at all (in send-as-I-go
// mode; site mode keeps a one-line reminder that it is on). A sync result
// is announced once and dismissed for good — it must not become a nag.
//
// Lives inside AppShell so every authed screen has the banner.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Cloud, CloudOff, RefreshCw, Check, ChevronDown, ChevronUp,
  ShieldAlert, X, AlertTriangle, Smartphone, Wifi,
} from 'lucide-react';
import { theme } from '../theme';
import {
  subscribeOutbox, drainOutbox, inspectOutbox,
  discardOutboxItems, adoptUnstampedItems,
  getSyncLog, syncLogHasUnseen, markSyncLogSeen, getSyncSeenAt,
  getDrainProgress, retryQuarantined, getSyncResume, clearSyncResume,
} from '../utils/outbox.js';
import { onPageWake, pageHiddenNow } from '../utils/pageWake.js';
import { queuedAtLabel } from '../utils/outbox-labels.js';
import {
  getSyncMode, setSyncMode, getAutoWifi, setAutoWifi, subscribeSyncMode,
  connectionType, autoSyncAllowedNow,
} from '../utils/syncMode.js';
// v0.177.0 — whether the browser has promised to keep what we are holding.
import {
  getStorageState, subscribeStorage, refreshEstimate, storageAdvice,
} from '../utils/persistStorage.js';

export default function OfflineBanner() {
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [pending, setPending] = useState([]);   // this sign-in's queued changes
  const [blocked, setBlocked] = useState([]);   // never replayed automatically
  const [log, setLog] = useState([]);           // what recent syncs did
  const [seenAt, setSeenAt] = useState(0);      // newest entry already acknowledged
  const [unseen, setUnseen] = useState(false);
  const [draining, setDraining] = useState(false);
  const [open, setOpen] = useState(false);
  // v0.171.0
  const [mode, setMode] = useState(() => getSyncMode());
  const [autoWifi, setAutoWifiState] = useState(() => getAutoWifi());
  const [progress, setProgress] = useState(() => getDrainProgress());
  const [lastStop, setLastStop] = useState(null);   // 'offline' | 'server' | 'device' | null
  const [conn, setConn] = useState(() => connectionType());
  const [storage, setStorage] = useState(() => getStorageState());
  // v0.256.0 — a sync that has not finished yet (see getSyncResume).
  const [resuming, setResuming] = useState(() => !!getSyncResume());

  // v0.255.0 — ONE queue read at a time. Every outbox change calls this, and a
  // sync changes the outbox once a row: with 71 photos queued the reads ran
  // side by side (each, before v0.255, holding every photo's bytes) and the
  // iPad closed the app. Asked while one runs → one more after it, and every
  // asker waits for that last one, so nobody reads a stale queue.
  const refreshRef = useRef({ running: null, again: false });
  const refresh = useCallback(() => {
    const r = refreshRef.current;
    if (r.running) { r.again = true; return r.running; }
    r.running = (async () => {
      try {
        do {
          r.again = false;
          try {
            const { pending: p, blocked: b } = await inspectOutbox();
            setPending(p);
            setBlocked(b);
            setLog(getSyncLog());
            setSeenAt(getSyncSeenAt());
            setUnseen(syncLogHasUnseen());
            setMode(getSyncMode());
            setAutoWifiState(getAutoWifi());
            setConn(connectionType());
            setResuming(!!getSyncResume());
            // Cheap, and it keeps the held-work warning honest about how much room is left.
            refreshEstimate().then(setStorage).catch(() => {});
          } catch (e) {
            console.warn('[offline-banner] queue read failed:', e);
          }
        } while (r.again);
      } finally {
        r.running = null;
      }
    })();
    return r.running;
  }, []);

  // Mount: read the queue once + subscribe to changes.
  useEffect(() => {
    let cancelled = false;
    const run = (evt) => {
      if (cancelled) return;
      if (evt && evt.type === 'progress') { setProgress({ ...evt.progress }); return; }
      refresh();
    };
    run();
    const unsub = subscribeOutbox(run);
    const unsubMode = subscribeSyncMode(() => { if (!cancelled) refresh(); });
    const unsubStore = subscribeStorage((s) => { if (!cancelled) setStorage(s); });
    return () => { cancelled = true; unsub(); unsubMode(); unsubStore(); };
  }, [refresh]);

  // A ref, not the `draining` state: the reconnect handler and the Sync
  // now button both call this, and a state value read through a stale
  // closure would let two drains replay the same rows concurrently.
  // (outbox.js also refuses a second concurrent drain; belt and braces.)
  const drainingRef = useRef(false);
  // v0.256.0 — how many back-off waits in a row (see the back-off below).
  const backoffRef = useRef(0);
  // upToId: carry on a site-mode Sync now with only what it was pressed for.
  const attemptDrain = useCallback(async (manual, upToId) => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    setDraining(true);
    setLastStop(null);
    try {
      const opts = { manual: !!manual };
      if (upToId != null) opts.upToId = upToId;
      const r = await drainOutbox(undefined, opts);
      setLastStop(r && r.stopped ? r.stopped : null);
      if (!(r && r.stopped)) backoffRef.current = 0;
    } finally {
      drainingRef.current = false;
      setDraining(false);
      await refresh();
    }
  }, [refresh]);

  // Auto-sync when the rules allow it (see syncMode.shouldAutoSync): always
  // in send-as-I-go mode, only on a positively reported Wi-Fi in site mode.
  const autoDrainIfAllowed = useCallback(async () => {
    if (!autoSyncAllowedNow()) return;
    const { pending: p } = await inspectOutbox();
    if (p.length > 0) attemptDrain(getSyncMode() === 'hold');
  }, [attemptDrain]);

  // v0.256.0 — a sync the iPad interrupted carries on by itself. Brett: "sync
  // on droppin stopped again. i think it was when the ipad went to sleep. We
  // cant have that." outbox.js keeps a note of a sync that has not finished;
  // while it is there this starts it again when the iPad wakes, when the
  // device says it is online, when the app is opened again, and on a back-off.
  const resumeIfWanted = useCallback(() => {
    const want = getSyncResume();
    setResuming(!!want);
    if (!want || drainingRef.current || pageHiddenNow()) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (getSyncMode() === 'hold') {
      // Site mode sends only what Sync now was pressed for, nothing queued after.
      if (want.manual) attemptDrain(true, want.upToId);
      else clearSyncResume();
    } else {
      attemptDrain(false);
    }
  }, [attemptDrain]);

  const kick = useCallback(() => {
    if (getSyncResume()) resumeIfWanted();
    else autoDrainIfAllowed();
  }, [resumeIfWanted, autoDrainIfAllowed]);

  // Back from a nap (screen lock, app switch, back/forward cache).
  useEffect(() => onPageWake(() => { backoffRef.current = 0; resumeIfWanted(); }), [resumeIfWanted]);

  // Stopped with the note still there: try again in 10 s, 30 s, 1 min, 2 min,
  // then every 2 min. A frozen page's timer waits; the wake above goes first.
  useEffect(() => {
    if (!lastStop || draining || !resuming) return undefined;
    const steps = [10000, 30000, 60000, 120000];
    const ms = steps[Math.min(backoffRef.current, steps.length - 1)];
    const timer = setTimeout(() => { backoffRef.current++; resumeIfWanted(); }, ms);
    return () => clearTimeout(timer);
  }, [lastStop, draining, resuming, resumeIfWanted]);

  // Online / offline events, and the connection-type change where the
  // browser reports one (Android: cellular → wifi is the moment site mode
  // has been waiting for).
  useEffect(() => {
    function on()  { setOnline(true); setConn(connectionType()); backoffRef.current = 0; kick(); }
    function off() { setOnline(false); }
    function connChange() { setConn(connectionType()); kick(); }
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    const c = typeof navigator !== 'undefined' ? navigator.connection : null;
    if (c && typeof c.addEventListener === 'function') c.addEventListener('change', connChange);
    return () => {
      window.removeEventListener('online',  on);
      window.removeEventListener('offline', off);
      if (c && typeof c.removeEventListener === 'function') c.removeEventListener('change', connChange);
    };
  }, [kick]);

  // Initial drain — in case there were leftovers when we mounted online, or a
  // sync the app was closed part-way through (v0.256.0).
  useEffect(() => {
    kick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only the changes from the sync the user hasn't acknowledged yet —
  // the strip must report on what just happened, not on the whole
  // 25-entry history behind it.
  const latest = log.filter(e => Number(e.at) > seenAt);
  const latestApplied  = latest.filter(e => e.outcome === 'applied').length;
  const latestRejected = latest.filter(e => e.outcome === 'rejected').length;

  function dismiss() {
    markSyncLogSeen();
    setUnseen(false);
    setOpen(false);
  }

  const hold = mode === 'hold';

  // A CLEAN result announces itself once and then gets out of the way —
  // PlanScreen drains after every defect save, so a success strip that
  // stuck around would be a permanent fixture. A result with a refusal
  // in it stays until the user actually acknowledges it (that is the
  // whole point of this release), as does anything left blocked.
  const cleanResult = unseen
    && !open
    && pending.length === 0
    && blocked.length === 0
    && latestRejected === 0;
  useEffect(() => {
    if (!cleanResult) return undefined;
    const timer = setTimeout(() => { markSyncLogSeen(); setUnseen(false); }, 6000);
    return () => clearTimeout(timer);
  }, [cleanResult]);

  // Only rows we can actually judge are discardable. A 'no-session' row
  // is blocked because DropPin cannot tell who is signed in — the row
  // itself may well be the user's own work. Offering to bin it there
  // would turn a temporary auth blip into permanent data loss; signing
  // in re-evaluates it instead.
  // v0.174.0 — rows the app refuses to send on its own but has KEPT: a server
  // refusal, or an image it could not read back. Both are retryable.
  const heldRows = blocked.filter(b => b.verdict === 'refused' || b.verdict === 'unreadable');
  const discardable = blocked.filter(b => b.verdict !== 'no-session');
  async function discardBlocked() {
    if (!discardable.length) return;
    await discardOutboxItems(discardable.map(b => b.id));
    await refresh();
  }

  // v0.174.0 — the row is still here WITH ITS PHOTO, so "try again" is a real
  // option rather than a condolence. Clears the quarantine marks and syncs.
  async function retryBlocked() {
    const n = await retryQuarantined(heldRows.map(b => b.id));
    await refresh();
    if (n > 0) attemptDrain(true);
  }

  async function sendUnstamped() {
    const n = await adoptUnstampedItems();
    await refresh();
    if (n > 0) attemptDrain(true);
  }

  function chooseMode(next) {
    setSyncMode(next);
    setMode(next);
    // Choosing to hold also stops a sync from carrying on by itself.
    if (next === 'hold') clearSyncResume();
    // Switching back to send-as-I-go with a queue waiting: send it.
    if (next === 'auto' && online && pending.length > 0) attemptDrain(false);
  }
  function chooseAutoWifi(on) {
    setAutoWifi(on);
    setAutoWifiState(on);
    if (on) autoDrainIfAllowed();
  }

  const nPending = pending.length;
  const nBlocked = blocked.length;
  const summary = summarise(pending);
  // v0.177.0 — only speaks up when work is actually being held on storage the
  // browser has not promised to keep.
  const advice = storageAdvice(storage, pending.length);
  const syncing = draining && progress && progress.active;

  // ── Calm state: nothing queued, nothing blocked, nothing new to say.
  // Site mode keeps a one-line reminder that it is on — silently holding
  // work is the one thing this mode must never do.
  if (online && nPending === 0 && nBlocked === 0 && !unseen && !hold) return null;

  // Pick the strip's tone from the most serious thing it has to report.
  let tone = theme.success;
  let Icon = Check;
  let message;

  if (syncing) {
    tone = theme.warn;
    Icon = RefreshCw;
    const at = Math.min(progress.total, progress.done + 1);
    message = (
      <>Sending <strong>{at} of {progress.total}</strong>{progress.current ? <> — {progress.current.title}</> : null}…</>
    );
  } else if (!online) {
    tone = theme.danger;
    Icon = CloudOff;
    message = nPending > 0
      ? (hold
        ? <>You're offline — <strong>{summaryText(summary)}</strong> saved on this phone. Sync when you're back on Wi-Fi.</>
        : <>You're offline — <strong>{summaryText(summary)}</strong> will sync when reception returns.</>)
      : <>You're offline. New changes will queue and sync when reception returns.</>;
  } else if (hold && nPending > 0) {
    tone = theme.warn;
    Icon = Smartphone;
    message = lastStop
      ? <>Sync stopped — <strong>{summaryText(summary)}</strong> still on this phone. {stopReason(lastStop, resuming)}</>
      : <>Site mode — <strong>{summaryText(summary)}</strong> saved on this phone. Sync when you're on Wi-Fi.</>;
  } else if (nPending > 0) {
    tone = theme.warn;
    Icon = Cloud;
    message = lastStop
      ? <>Sync paused — <strong>{summaryText(summary)}</strong> left. {stopReason(lastStop, resuming)}</>
      : <><strong>{summaryText(summary)}</strong> waiting to sync.</>;
  } else if (nBlocked > 0) {
    tone = theme.danger;
    Icon = ShieldAlert;
    message = <><strong>{nBlocked} change{nBlocked === 1 ? '' : 's'}</strong> {nBlocked === 1 ? 'is' : 'are'} still here, not sent — {blockedReason(blocked)}.</>;
  } else if (latestRejected > 0) {
    tone = theme.danger;
    Icon = AlertTriangle;
    message = <>Synced, but <strong>{latestRejected} change{latestRejected === 1 ? '' : 's'}</strong> {latestRejected === 1 ? 'was' : 'were'} refused — kept on this device, nothing lost.</>;
  } else if (unseen) {
    message = latestApplied > 0
      ? <>Synced — <strong>{latestApplied} change{latestApplied === 1 ? '' : 's'}</strong> {latestApplied === 1 ? 'is' : 'are'} now live.</>
      : <>Synced. Nothing was waiting.</>;
  } else {
    // hold mode, nothing queued: the quiet reminder.
    tone = theme.textLight;
    Icon = Smartphone;
    message = <>Site mode on — changes stay on this phone until you tap Sync.</>;
  }

  const showDetails = nPending > 0 || nBlocked > 0 || log.length > 0 || hold;
  const pct = syncing && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div style={{ borderBottom: `1px solid ${tone}`, background: theme.bg }}>
      <div style={{ ...baseRow, color: tone }}>
        {syncing && <style>{`@keyframes droppin-sync-spin { to { transform: rotate(360deg); } }`}</style>}
        <Icon size={14} style={syncing ? { animation: 'droppin-sync-spin 1s linear infinite' } : undefined} />
        <span style={{ flex: 1 }}>{message}</span>

        {showDetails && (
          <button onClick={() => setOpen(o => !o)} style={pillBtn(tone)}>
            {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {open ? 'Hide' : 'Details'}
          </button>
        )}

        {online && nPending > 0 && !syncing && (
          <button onClick={() => attemptDrain(true)} disabled={draining} style={{ ...pillBtn(tone), opacity: draining ? 0.5 : 1, cursor: draining ? 'not-allowed' : 'pointer' }}>
            <RefreshCw size={11} /> {lastStop ? 'Retry' : 'Sync now'}
          </button>
        )}

        {online && nPending === 0 && nBlocked === 0 && unseen && (
          <button onClick={dismiss} title="Dismiss" style={{ ...pillBtn(tone), padding: '3px 6px' }}>
            <X size={11} />
          </button>
        )}
      </div>

      {syncing && (
        <div style={{ height: 3, background: theme.border }}>
          <div style={{ height: '100%', width: `${pct}%`, background: tone, transition: 'width 0.3s' }} />
        </div>
      )}

      {open && (
        <div style={panel}>
          {advice && (
            <div style={{
              marginBottom: 12, padding: '8px 10px', borderRadius: 6,
              border: `1px solid ${theme.warn}`, color: theme.warn,
              fontSize: 11, lineHeight: 1.5,
            }}>
              {advice.text}
            </div>
          )}

          <Section title="Sync settings">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              <ModeButton active={!hold} onClick={() => chooseMode('auto')} icon={<Cloud size={12} />}>
                Send as I go
              </ModeButton>
              <ModeButton active={hold} onClick={() => chooseMode('hold')} icon={<Smartphone size={12} />}>
                Site mode — hold until I tap Sync
              </ModeButton>
            </div>
            {hold && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: theme.text, cursor: 'pointer' }}>
                <input type="checkbox" checked={autoWifi} onChange={(e) => chooseAutoWifi(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Wifi size={12} /> Also send automatically on Wi-Fi</span>
                  <span style={{ display: 'block', fontSize: 11, color: theme.textLight, marginTop: 2, lineHeight: 1.5 }}>
                    Only where the phone reports its connection (Android). An iPhone can't say, so there you tap Sync now.
                    {' '}This phone reports: <strong>{connLabel(conn)}</strong>.
                  </span>
                </span>
              </label>
            )}
            <div style={{ fontSize: 11, color: theme.textLight, marginTop: 6, lineHeight: 1.5 }}>
              {hold
                ? 'Pins, photos and replies save on this phone instantly and go up together when you sync. Sharing a report or inviting a contact still needs a connection.'
                : 'Changes go up as you make them; anything that fails to send is kept and retried when reception returns.'}
            </div>
          </Section>

          <Section
            title={`Waiting to sync (${nPending})${summary.bytes > 0 ? ` · ${fmtBytes(summary.bytes)}` : ''}`}
            empty="Nothing waiting."
          >
            {pending.map(row => (
              <Row key={row.id} title={row.title} detail={row.detail} meta={rowMeta(row)} />
            ))}
          </Section>

          {nBlocked > 0 && (
            <Section title={`Not applied (${nBlocked})`} tone={theme.danger}>
              <div style={{ fontSize: 11, color: theme.textLight, marginBottom: 6, lineHeight: 1.5 }}>
                {blockedExplanation(blocked)}
              </div>
              {blocked.map(row => (
                <Row
                  key={row.id}
                  title={row.title}
                  detail={row.detail}
                  note={row.message}
                  meta={rowMeta(row)}
                  tone={theme.danger}
                />
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {heldRows.length > 0 && (
                  <button onClick={retryBlocked} style={pillBtn(theme.brand)}>
                    <RefreshCw size={11} /> Try {heldRows.length === 1 ? 'it' : `these ${heldRows.length}`} again
                  </button>
                )}
                {blocked.some(b => b.verdict === 'unstamped') && (
                  <button onClick={sendUnstamped} style={pillBtn(theme.warn)}>
                    These are mine — send them
                  </button>
                )}
                {discardable.length > 0 && (
                  <button onClick={discardBlocked} style={pillBtn(theme.danger)}>
                    Discard {discardable.length === 1 ? 'it' : `${discardable.length} of them`}
                  </button>
                )}
              </div>
            </Section>
          )}

          {log.length > 0 && (
            <Section title="Recent sync activity">
              {log.map((e, i) => (
                <Row
                  key={`${e.at}-${i}`}
                  title={e.title}
                  detail={e.detail}
                  meta={stampLabel(e.at)}
                  tone={e.outcome === 'rejected' ? theme.danger : undefined}
                  badge={e.outcome === 'rejected' ? 'Refused' : 'Applied'}
                  note={e.outcome === 'rejected' ? e.message : null}
                />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

// ── Counting in the product's nouns ──────────────────────────────────
function summarise(rows) {
  const s = { pins: 0, photos: 0, other: 0, bytes: 0, total: 0 };
  for (const r of rows || []) {
    s.total++;
    if (r.kind === 'photo') s.photos++;
    else if (r.kind === 'pin') s.pins++;
    else s.other++;
    s.bytes += Number(r.bytes) || 0;
  }
  return s;
}

function summaryText(s) {
  const parts = [];
  if (s.pins)   parts.push(`${s.pins} pin${s.pins === 1 ? '' : 's'}`);
  if (s.photos) parts.push(`${s.photos} photo${s.photos === 1 ? '' : 's'}`);
  if (s.other)  parts.push(`${s.other} other change${s.other === 1 ? '' : 's'}`);
  if (!parts.length) return `${s.total} change${s.total === 1 ? '' : 's'}`;
  const txt = parts.join(', ');
  return s.photos && s.bytes > 0 ? `${txt} (${fmtBytes(s.bytes)})` : txt;
}

export function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${Math.round(b / 1024)} KB`;
  const mb = b / 1048576;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

function rowMeta(row) {
  const when = queuedAtLabel(row.queuedAt);
  return row.kind === 'photo' && row.bytes > 0 ? `${fmtBytes(row.bytes)} · ${when}` : when;
}

// v0.256.0 — `resuming`: the banner will start it again by itself, so say so
// instead of asking for another press.
function stopReason(stopped, resuming) {
  const carry = resuming ? ' It carries on by itself.' : '';
  if (stopped === 'server') return 'The server is not answering right now.' + carry;
  // v0.255.0 — the photo store did not answer; nothing was sent or lost.
  if (stopped === 'device') {
    return resuming
      ? 'The photo store on this device did not answer; nothing was lost.' + carry
      : 'The photo store on this device did not open — press Sync now again.';
  }
  return 'The connection dropped part-way.' + carry;
}

function connLabel(type) {
  if (!type) return "can't tell";
  if (type === 'wifi') return 'Wi-Fi';
  if (type === 'cellular') return 'mobile data';
  if (type === 'ethernet') return 'wired';
  return type;
}

// ── Copy for blocked changes ─────────────────────────────────────────
// Deliberately does NOT name the other identity. Telling the person
// holding the device who else has been signed in here is the same leak
// in the opposite direction.
function blockedReason(blocked) {
  const kinds = new Set(blocked.map(b => b.verdict));
  // v0.174.0 — these two are KEPT on the device, and the wording has to say so.
  if (kinds.size === 1 && kinds.has('refused'))    return 'the server would not take them — they are still saved here';
  if (kinds.size === 1 && kinds.has('unreadable')) return 'their image could not be read back — they are still listed here';
  if (kinds.has('refused') || kinds.has('unreadable')) return 'they are still saved on this device, waiting on you';
  if (kinds.size === 1 && kinds.has('unstamped')) return "DropPin can't confirm who made them";
  if (kinds.size === 1 && kinds.has('no-session')) return 'nobody is signed in';
  return 'they were made under a different sign-in';
}

function blockedExplanation(blocked) {
  const kinds = new Set(blocked.map(b => b.verdict));
  const parts = [];
  if (kinds.has('refused')) {
    parts.push('The server would not accept these. NOTHING HAS BEEN DELETED — they are still on this device with their photos. The reason the server gave is on each line; Try again once it looks fixable.');
  }
  if (kinds.has('unreadable')) {
    parts.push('These photos could not be read back from this device\u2019s storage. They are still listed rather than dropped, so you know exactly which ones to re-take.');
  }
  if (kinds.has('mismatch')) {
    parts.push('Some of these were queued while a different account was signed in on this device. Applying them now would record them against you, so DropPin has left them alone.');
  }
  if (kinds.has('unstamped')) {
    parts.push('Some of these were queued before DropPin started recording who made a change, so it cannot confirm they are yours. Send them only if you recognise them.');
  }
  if (kinds.has('no-session')) {
    parts.push('Nothing can be sent while no account is signed in. Sign in and these will be checked again.');
  }
  return parts.join(' ');
}

function stampLabel(at) {
  const t = Number(at);
  if (!Number.isFinite(t) || t <= 0) return '';
  try {
    return new Date(t).toLocaleString('en-AU', {
      day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    });
  } catch (_e) { return ''; }
}

// ── Bits ─────────────────────────────────────────────────────────────
function Section({ title, tone, children, empty }) {
  const hasChildren = Array.isArray(children) ? children.some(Boolean) : !!children;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
        color: tone || theme.textLight, marginBottom: 6,
      }}>
        {title}
      </div>
      {hasChildren
        ? children
        : <div style={{ fontSize: 12, color: theme.textLight }}>{empty || '—'}</div>}
    </div>
  );
}

function ModeButton({ active, onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 10px', fontSize: 12, fontWeight: 600,
        background: active ? theme.brand : 'transparent',
        color: active ? '#fff' : theme.text,
        border: `1px solid ${active ? theme.brand : theme.border}`,
        borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
      }}
    >{icon}{children}</button>
  );
}

function Row({ title, detail, meta, tone, badge, note }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 8,
      padding: '5px 0', borderTop: `1px solid ${theme.border}`,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: tone || theme.text }}>{title}</div>
        {(detail || note) && (
          <div style={{ fontSize: 11, color: tone || theme.textLight, marginTop: 2 }}>
            {[detail, note].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>
      {badge && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: tone || theme.success,
          border: `1px solid ${tone || theme.success}`, borderRadius: 999,
          padding: '1px 7px', whiteSpace: 'nowrap',
        }}>{badge}</span>
      )}
      <span style={{ fontSize: 11, color: theme.textLight, whiteSpace: 'nowrap' }}>{meta}</span>
    </div>
  );
}

const baseRow = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '6px 16px', fontSize: 12,
};

const panel = {
  padding: '10px 16px 14px',
  background: theme.surface,
  borderTop: `1px solid ${theme.border}`,
  maxHeight: '46vh',
  overflowY: 'auto',
};

function pillBtn(tone) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', fontSize: 11, fontWeight: 600,
    background: 'transparent', color: tone,
    border: `1px solid ${tone}`, borderRadius: 999,
    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  };
}
